import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
// eslint-disable-next-line import/no-unresolved
import { Content } from '@plone/types';
import { CardProps } from '@designsystem/stories/03-molecules/Card';
import { Listing } from '@designsystem/stories/04-organisms/Listing';
import Card from '@fhnw/components/Card/Card';
import {
  transformToCard,
  NormalizableContent,
} from '@fhnw/helpers/DesignSystem/cardTransformers';
import { getListingImageSizes } from '@fhnw/helpers/DesignSystem/imageSizes';
import type { ButtonSchemaData, MediaSchemaData } from '@fhnw/types/schemaData';
import { insertTeaserCard } from '../insertTeaserCard';
import TeaserCard, { TEASER_CARD_UID } from '../components/TeaserCard';
import { messages } from '../messages';

export interface GridTemplateProps {
  items: Content[];
  list?: boolean;
  useTable?: boolean;
  useCards?: boolean;
  showTeaserImage?: boolean;
  showTeaserText?: boolean;
  activateFilter?: boolean;
  showTeaserCard?: boolean;
  teaserCardPosition?: number | string;
  teaserCardTitle?: string;
  teaserCardText?: string;
  teaserCardMedia?: MediaSchemaData;
  teaserCardActions?: ButtonSchemaData[];
}

// Placeholder entry inside the cards array; CardComponent swaps it for the
// actual TeaserCard. Module-level so its identity is stable across renders.
const TEASER_CARD_MARKER: CardProps = {
  type: 'teaser',
  uid: TEASER_CARD_UID,
  title: '',
};

const GridTemplate = (props: GridTemplateProps) => {
  const intl = useIntl();
  const {
    items,
    list,
    useTable,
    useCards,
    showTeaserImage,
    showTeaserText,
    activateFilter,
    showTeaserCard,
    teaserCardPosition,
    teaserCardTitle,
    teaserCardText,
    teaserCardMedia,
    teaserCardActions,
  } = props;

  const university = useSelector(
    (state: { content: { data: Content } }) =>
      state.content.data['@components']?.university,
  );

  const style = list
    ? useTable
      ? 'table'
      : 'list'
    : !activateFilter && useCards === false && !activateFilter
      ? 'tile'
      : undefined;
  const cards = useMemo(
    () =>
      (items || []).map((item) =>
        transformToCard(
          item as NormalizableContent,
          intl,
          item?.['@type'] === 'Occasion' ? 'event' : undefined,
          style,
          university as NormalizableContent,
          undefined,
          getListingImageSizes(item?.['@type'], style, list),
        ),
      ),
    [items, intl, list, style, university],
  );
  const type = cards[0]?.type || 'base';

  const info_columns = useMemo(() => {
    const columns: string[] = [];
    if (list && useTable) {
      cards.forEach((card) => {
        if (!card.info) return;

        let prev: string | null = null;
        card.info.forEach((info_item) => {
          if (!info_item?.key) return;

          if (!columns.includes(info_item.key)) {
            if (prev && columns.includes(prev)) {
              const prev_index = columns.indexOf(prev);
              columns.splice(prev_index + 1, 0, info_item.key);
            } else if (!prev) {
              columns.unshift(info_item.key);
            } else {
              columns.push(info_item.key);
            }
          }

          prev = info_item.key;
        });
      });
    }

    return columns;
  }, [cards, list, useTable]);

  const header = useMemo(() => {
    if (info_columns.length === 0) {
      return undefined;
    }

    const leadingColumn =
      type === 'course'
        ? intl.formatMessage(messages.DegreeProgram)
        : type === 'training'
          ? intl.formatMessage(messages.ContinuingEducation)
          : type === 'person'
            ? intl.formatMessage(messages.Name)
            : intl.formatMessage(messages.Designation);

    return [leadingColumn, ...info_columns].map((key) => ({ label: key }));
  }, [info_columns, intl, type]);

  const CardComponent = useCallback(
    (item: CardProps) => {
      let card = item;

      if (item.uid === TEASER_CARD_UID) {
        return (
          <TeaserCard
            className={item.className}
            title={teaserCardTitle}
            text={teaserCardText}
            media={teaserCardMedia}
            actions={teaserCardActions}
            imageSizes={getListingImageSizes('teaser', style, list)}
          />
        );
      }

      if (
        item.style === 'table' &&
        item?.info &&
        info_columns.some(
          (key) => !item.info?.find((info_item) => info_item.key === key),
        )
      ) {
        const info = [...item.info];
        info_columns.forEach((key, i) => {
          if (!info.find((info_item) => info_item.key === key)) {
            info.splice(i, 0, { key: key, value: undefined });
          }
        });
        card = { ...item, info };
      }

      return (
        <Card
          showImage={showTeaserImage}
          showText={showTeaserText}
          card={card}
        />
      );
    },
    [
      info_columns,
      list,
      style,
      showTeaserImage,
      showTeaserText,
      teaserCardTitle,
      teaserCardText,
      teaserCardMedia,
      teaserCardActions,
    ],
  );

  if (cards.length === 0) {
    return null;
  }

  return (
    <Listing
      list={list && !type.endsWith('-list')}
      type={type}
      style={style}
      items={
        showTeaserCard
          ? insertTeaserCard(cards, TEASER_CARD_MARKER, teaserCardPosition)
          : cards
      }
      card={CardComponent}
      header={header}
    />
  );
};

export default GridTemplate;
